import { createContext, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createApiClient, getBalance, type ApiClient } from '../api';
import { config } from '../../config';
import { useSession } from '../session';
import { emit, isIncomingEvent } from '../postMessage';
import type { WalletContextValue, WalletState } from './types';

// Strict-mode-safe seed — state never carries an api error across remounts.
const INITIAL: WalletState = {
  balance_cents: null,
  currency: 'USD',
  loading: true,
  error: null,
};

export const WalletContext = createContext<WalletContextValue | null>(null);

export function WalletProvider({ children }: { children: ReactNode }) {
  const session = useSession();
  const [state, setState] = useState<WalletState>(INITIAL);

  // Own API client — own responsibilities. Re-created only when session.token changes.
  const client = useMemo<ApiClient>(
    () => createApiClient({ baseUrl: config.apiUrl, getToken: () => session.token }),
    [session.token],
  );

  const clientRef = useRef(client);
  clientRef.current = client;

  // Track the previous balance across refreshes without triggering
  // re-renders — useRef avoids the re-render loop that a dependency on
  // `state.balance_cents` would introduce.
  const prevBalanceRef = useRef<number | null>(null);

  const refresh = useCallback(async () => {
    if (!session.token) {
      setState((s) => ({ ...s, loading: false, error: null }));
      return;
    }
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const res = await getBalance(clientRef.current);
      setState({
        balance_cents: res.balance_cents,
        currency: res.currency,
        loading: false,
        error: null,
      });

      // Dev-only: log balance transitions so debit/credit are easy to
      // eyeball during purchase / sell flows. Delta tells you whether the
      // wallet went up (credit) or down (debit) since the last refresh.
      if (import.meta.env.DEV) {
        const prev = prevBalanceRef.current;
        const delta = prev == null ? null : res.balance_cents - prev;
        const kind =
          delta == null
            ? 'initial'
            : delta > 0
              ? 'credit'
              : delta < 0
                ? 'debit'
                : 'no-change';
        console.log(
          `%c[wallet] ${kind}`,
          'color: #38BDF8; font-weight: 600',
          {
            balance_cents: res.balance_cents,
            currency: res.currency,
            delta_cents: delta,
          },
        );
      }
      prevBalanceRef.current = res.balance_cents;

      emit('balance_updated', {
        balance_cents: res.balance_cents,
        currency: res.currency,
      });
    } catch (err) {
      setState((s) => ({
        ...s,
        loading: false,
        error: err instanceof Error ? err.message : 'balance fetch failed',
      }));
    }
  }, [session.token]);

  // Initial fetch — once per session.
  const didInit = useRef(false);
  useEffect(() => {
    if (didInit.current) return;
    didInit.current = true;
    void refresh();
  }, [refresh]);

  // Parent → iframe: { source: 'collectibles-parent', type: 'balance_changed' }.
  useEffect(() => {
    function onMessage(e: MessageEvent) {
      if (!isIncomingEvent(e.data)) return;
      if (e.data.type === 'balance_changed') void refresh();
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [refresh]);

  const value = useMemo<WalletContextValue>(
    () => ({ ...state, refresh }),
    [state, refresh],
  );

  return <WalletContext value={value}>{children}</WalletContext>;
}
